import { Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react'
import { MdError } from '@react-icons/all-files/md/MdError'
import { RiMoreLine } from '@react-icons/all-files/ri/RiMoreLine'
import { useLocalRepositories } from '~/hooks/useLocalRepositories'
import { cm } from '~/utils/utils'

export function LocalRepositorySection() {
  const {
    storedHandles,
    localErrorMessage,
    processingLocalAction,
    handleIds,
    handleSelectLocalRepository,
    handleRemoveLocalRepository,
    getNameStyle,
    getSecurityStatusText
  } = useLocalRepositories()

  return (
    <div className='px-4'>
      {/* Error message display */}
      {localErrorMessage && (
        <div className='flex items-center mb-4 text-sm text-red-600'>
          <MdError className='mr-1' />
          {localErrorMessage}
        </div>
      )}

      {storedHandles.length > 0 && (
        <>
          {/* Table header - Only display when there are repositories */}
          <div className='flex justify-between items-center mb-4 px-2'>
            <div className='font-mono font-semibold text-sm text-black/65 leading-4 select-none'>
              Repository name
            </div>
            <div className='font-mono font-semibold text-sm text-black/65 leading-4 select-none'>
              Actions
            </div>
          </div>

          {/* Local Repository list */}
          <div className='space-y-4'>
            {storedHandles.map(storedHandle => {
              const handleInfo = handleIds.find(h => h.id === storedHandle.id)
              const securityStatusText = getSecurityStatusText(handleInfo?.securityStatus)

              return (
                <div
                  key={storedHandle.id}
                  className='flex justify-between items-center py-3 px-2 hover:bg-[#E8E8E8] rounded-[12px] transition-colors'
                >
                  <div className='flex-1'>
                    <div
                      className={cm(
                        'text-base text-[rgba(0,0,0,0.88)] font-medium flex items-center',
                        getNameStyle(handleInfo?.securityStatus)
                      )}
                    >
                      {storedHandle.handle.name}
                    </div>
                    {/* Status information displayed below repo name */}
                    {securityStatusText && (
                      <div
                        className={cm(
                          'text-xs mt-1 font-semibold',
                          handleInfo?.securityStatus === 'THREATS_FOUND'
                            ? 'text-[#EA462D]'
                            : handleInfo?.securityStatus === 'SCANNING'
                              ? 'text-black/45 font-medium'
                              : ''
                        )}
                      >
                        {securityStatusText}
                      </div>
                    )}
                  </div>

                  {/* Three dots button */}
                  <Popover
                    placement='bottom-end'
                    classNames={{
                      content:
                        'rounded-[12px] shadow-popover h-12 w-[170px] flex items-center justify-center',
                      trigger: 'aria-expanded:!opacity-100 aria-expanded:!scale-100'
                    }}
                    offset={-12}
                    crossOffset={6}
                  >
                    <PopoverTrigger>
                      <div className='w-9 h-9 flex justify-center items-center'>
                        <button className=''>
                          <RiMoreLine className='h-5 w-5 text-[rgba(0,0,0,0.45)]' />
                        </button>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className='p-2 min-w-0'>
                      <button
                        onClick={() => handleRemoveLocalRepository(storedHandle.id)}
                        className='text-base font-medium flex items-center justify-center gap-2 w-full px-3 py-2 text-[#C22C15] hover:bg-[rgba(0,0,0,0.04)] !rounded-lg transition-colors select-none'
                      >
                        Delete Repository
                      </button>
                    </PopoverContent>
                  </Popover>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Add Repository button */}
      <div className='mt-4'>
        <button
          onClick={handleSelectLocalRepository}
          disabled={processingLocalAction}
          className={cm(
            'w-[200px] h-[36px] rounded-[12px] border border-[#D6D6D6] flex items-center justify-center gap-2 transition-colors',
            processingLocalAction ? 'opacity-50 cursor-wait' : 'hover:bg-gray-50'
          )}
          aria-label='Add Local Repository Button'
        >
          {processingLocalAction ? (
            <span className='select-none'>Processing...</span>
          ) : (
            <>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
                className='w-[18px] h-[18px]'
              >
                <path
                  d='M8.25 9.75H3.75V8.25H8.25V3.75H9.75V8.25H14.25V9.75H9.75V14.25H8.25V9.75Z'
                  fill='black'
                  fillOpacity='0.45'
                />
              </svg>
              <span className='text-base font-medium text-[#262626] select-none'>
                Add Local Repository
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  )
}
